import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';
import { Grid } from '@material-ui/core';
import { getOptions } from '@ourbranch/lookups';

import { getPersonAge, getFormikId } from '../helpers';
import Section from 'core/components/section';
import { Card } from 'core/components/card';
import { Label } from 'core/components/label';
import { tooltipHoverTexts, lockedValuesTooltipTexts } from 'core/helpers/constants';
import { Tooltip } from 'core/components/tooltip';
import lockIconOrange from 'core/assets/svg/ic-locked.svg';
import questionIconOrange from 'core/assets/svg/info-small.svg';
import Field from 'core/components/form/form.v2';
import PipCoveragesInfoCard from './pip-coverages-info-card';

import useStyles from './pip-coverages.styles';

const PipCoverages = () => {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext();
  const { drivers, nonDrivers } = values;
  const { policyLimitPIPME, policyLimitPIPACR, policyLimitPIPWL } = values.autoCoverage;

  const residentsList = (nonDrivers || []).concat(drivers).sort((a) => (a.isPrimary ? -1 : 1));
  const lockAttendantCare = ['UNLIMITED', 'OPTOUT', 'NONE'].includes(policyLimitPIPME);
  const residentsOver60 = (nonDrivers || [])
    .concat(drivers)
    .some((resident) => getPersonAge(resident.dateOfBirth) > 60);

  useEffect(() => {
    if (lockAttendantCare && policyLimitPIPME === 'UNLIMITED') {
      setFieldValue('autoCoverage.policyLimitPIPACR', true);
    }
    if ((lockAttendantCare && policyLimitPIPME === 'OPTOUT') || policyLimitPIPACR === 'NONE') {
      setFieldValue('autoCoverage.policyLimitPIPACR', false);
    }

    if (policyLimitPIPME === '250000W/EXCLUDED') {
      setFieldValue(`drivers[${drivers.findIndex((driver) => driver.isPrimary)}].pipOptOut`, true);
      setFieldValue(`drivers[${drivers.findIndex((driver) => driver.isPrimary)}].pipHasQHC`, true);
    }

    // no pipOptOut unless option 4 or 6
    if (
      policyLimitPIPME !== '250000W/EXCLUDED' &&
      policyLimitPIPME !== 'OPTOUT' &&
      drivers.some((driver) => driver.pipOptOut)
    ) {
      residentsList.forEach((resident) => {
        setFieldValue(`${getFormikId(resident, values)}.pipOptOut`, false);
      });
    }

    // no hasQHC unless option 4
    if (policyLimitPIPME !== '250000W/EXCLUDED' && drivers.some((driver) => driver.pipHasQHC)) {
      residentsList.forEach((resident) => {
        setFieldValue(`${getFormikId(resident, values)}.pipHasQHC`, false);
      });
    }
    // everyone opts out for option 6
    if (policyLimitPIPME === 'OPTOUT') {
      residentsList.forEach((resident) => {
        setFieldValue(`${getFormikId(resident, values)}.pipOptOut`, true);
      });
    }

    // if option 4 and hasQHC, set pipOptOut to match
    if (policyLimitPIPME === '250000W/EXCLUDED' && drivers.some((driver) => driver.pipHasQHC)) {
      residentsList.forEach((resident) => {
        if (resident.pipHasQHC) {
          setFieldValue(`${getFormikId(resident, values)}.pipOptOut`, true);
        } else {
          setFieldValue(`${getFormikId(resident, values)}.pipOptOut`, false);
        }
      });
    }

    if (policyLimitPIPWL) {
      residentsList.forEach((resident) => {
        if (getPersonAge(resident.dateOfBirth) > 60) {
          setFieldValue(`${getFormikId(resident, values)}.waivedPIPWL`, true);
        }
      });
    }
    if (!policyLimitPIPWL) {
      residentsList.forEach((resident) => {
        if (getPersonAge(resident.dateOfBirth) > 60) {
          setFieldValue(`${getFormikId(resident, values)}.waivedPIPWL`, false);
        }
      });
    }

    if (!residentsOver60) {
      setFieldValue('autoCoverage.policyLimitPIPWL', false);
    }
  }, [policyLimitPIPME, policyLimitPIPWL, policyLimitPIPACR, residentsOver60, drivers, nonDrivers]);

  return (
    <Section title="Personal injury protection (PIP) coverage" type="SubSection" id="personalInjuryProtectionSection">
      <Card type="secondary">
        <Grid className={classes.pipCoverageCard} container xs={12}>
          <Grid container xs={12}>
            <Label type="formSubTitle">PIP medical limit</Label>
          </Grid>
          <Grid container className={classes.pipMEContainer}>
            {getOptions('policyLimitPIPME').map((opt, index) => (
              <Grid key={index} className={classes.columnContainer}>
                <Grid className={classes.radioContainer}>
                  <Field
                    type="radio"
                    name={'autoCoverage.policyLimitPIPME'}
                    mode="dark"
                    tooltipLabel="More Info"
                    options={[{ label: opt.value, value: opt.id, id: opt.id }]}
                  />
                  {opt?.hint && (
                    <Grid className={classes.rowContainer}>
                      <p className={classes.pipEligibilityText}>{opt.hint}</p>
                      <Tooltip
                        smallIcon={{ src: questionIconOrange, alt: 'info icon' }}
                        iconSize="smallIcon"
                        text={tooltipHoverTexts.policyLimitPIPME(opt.id)}
                        placement="right"
                      />
                    </Grid>
                  )}
                </Grid>
                {opt?.hint && policyLimitPIPME === opt.id && (
                  <PipCoveragesInfoCard opt={opt} residentsList={residentsList} />
                )}
              </Grid>
            ))}
          </Grid>
          <div className={classes.separator} />
          <Grid container xs={12}>
            <Label type="formSubTitle">Additional PIP coverages</Label>
          </Grid>
          <Grid container xs={12} className={classes.rowContainer}>
            <Grid xs={6} container alignItems="center">
              <Field
                label="Attendant care coverage"
                type="checkbox"
                name="autoCoverage.policyLimitPIPACR"
                tooltipText={tooltipHoverTexts.policyLimitPIPACR}
                tooltipLabel="More Info"
                mode="dark"
                disabled={lockAttendantCare}
              />
              {lockAttendantCare && (
                <Tooltip
                  text={lockedValuesTooltipTexts.attendantCareCoverage}
                  placement="right"
                  iconSize="icon16"
                  smallIcon={{ src: lockIconOrange, alt: 'Lock icon' }}
                />
              )}
            </Grid>
            <Grid xs={6} container alignItems="center">
              <Field
                label="Waive work-loss coverage"
                type="checkbox"
                name="autoCoverage.policyLimitPIPWL"
                tooltipText={tooltipHoverTexts.policyLimitPIPWL}
                tooltipLabel="More Info"
                mode="dark"
                disabled={!residentsOver60}
              />{' '}
              {!residentsOver60 && (
                <Tooltip
                  text={lockedValuesTooltipTexts.waivedWorkLossCoverage}
                  placement="right"
                  iconSize="icon16"
                  smallIcon={{ src: lockIconOrange, alt: 'Lock icon' }}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
      </Card>
    </Section>
  );
};

export default PipCoverages;
